import React, { useMemo } from 'react'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import ItemPreview from '@aytm/app/swidget/base/questions/Ecommerce/Amazon/ViewComponents/ItemPreview.tsx'
import style from './Main.module.scss'
import itemStyle from './ItemPreview.module.scss'
import { IEcommerceQuestionSchema, EcommerceResponse } from '../../types'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import PromoRow from './PromoRow'
import TrendingRow from './TrendingRow'

interface IMainProps {
  schema: IEcommerceQuestionSchema
  ceilWidth: number
  mainWidth: number
  response: EcommerceResponse
  onProductAction: (qnt: -1 | 1, index: string, subscribe?: number) => void
  onSpin: (event: React.KeyboardEvent, index: string, subscribe?: number) => void
  onItemSelect: (index: number, bannerPlacement?: string) => void
  saveScrollPoint: (event: React.MouseEvent) => void
  productsPerRow: number
}

const Main = ({
  schema,
  ceilWidth,
  mainWidth,
  response,
  onProductAction,
  onSpin,
  onItemSelect,
  saveScrollPoint,
  productsPerRow
}: IMainProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()
  const sponsoredSize = schema.answers.filter(answer => answer.featured).length
  const promoSize = schema.answers.filter(answer => answer.banner).length
  const firstProductCount = viewport.mobile ? 6 : productsPerRow * 2

  const results = useMemo(
    () =>
      schema.answers
        .filter(answer => !answer.featured && !answer.banner)
        .filter(answer => !answer.na)
        .filter(answer => null === answer.parent_product)
        .slice(0, firstProductCount),
    [schema, firstProductCount]
  )

  const moreResults = useMemo(
    () =>
      schema.answers
        .filter(answer => !answer.featured && !answer.banner)
        .filter(answer => !answer.na)
        .filter(answer => null === answer.parent_product),
    [schema]
  )

  return (
    <>
      {promoSize > 0 ? (
        <PromoRow
          mainWidth={mainWidth}
          schema={schema}
          response={response}
          onProductAction={onProductAction}
          onSpin={onSpin}
          onItemSelect={onItemSelect}
          saveScrollPoint={saveScrollPoint}
        />
      ) : null}
      {sponsoredSize > 0 ? (
        <>
          <div
            className={style.results}
            dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'amazon', 'results') }}
          />
          <div className={`${style.content} ${viewport.mobile ? style.Mobile : ''}`}>
            {results.map((answer, index) => (
              <>
                {null === answer.parent_product ? (
                  <ItemPreview
                    key={index}
                    schema={answer}
                    questionSchema={schema}
                    onProductAction={qnt => onProductAction(qnt, answer.id)}
                    onSpin={event => onSpin(event, answer.id)}
                    onItemSelect={() => onItemSelect(answer.index)}
                    width={ceilWidth}
                    className={'focus' == schema.mode_type && !answer.focused ? itemStyle.disabled : ''}
                    data-testid="SW-SH-Item"
                    response={response.answers.find(a => a.id === answer.id && !a.subscribe)}
                    index={answer.index}
                    findability={'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)}
                    saveScrollPoint={saveScrollPoint}
                  />
                ) : null}
              </>
            ))}
          </div>

          <TrendingRow
            schema={schema}
            response={response}
            // ceilWidth={ceilWidth}
            productsPerRow={productsPerRow}
            bannerPlacement="featured"
            maxWidth={mainWidth - 15}
            onProductAction={onProductAction}
            onSpin={onSpin}
            onItemSelect={onItemSelect}
            saveScrollPoint={saveScrollPoint}
          />

          {moreResults.some((_, index) => sponsoredSize <= 0 || index >= firstProductCount) && (
            <div className={style.results}>{t('questions', 'ec', 'amazon', 'moreResults')}</div>
          )}
        </>
      ) : (
        <div
          className={style.results}
          dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'amazon', 'results') }}
        />
      )}
      <div className={`${style.content} ${viewport.mobile ? style.Mobile : ''}`}>
        {moreResults.map((answer, index) =>
          sponsoredSize <= 0 || index >= firstProductCount ? (
            <ItemPreview
              key={index}
              schema={answer}
              questionSchema={schema}
              onProductAction={qnt => onProductAction(qnt, answer.id)}
              onSpin={event => onSpin(event, answer.id)}
              onItemSelect={() => onItemSelect(answer.index)}
              width={ceilWidth}
              className={'focus' == schema.mode_type && !answer.focused ? itemStyle.disabled : ''}
              data-testid="SW-SH-Item"
              response={response.answers.find(r => r.id === answer.id && !r.subscribe)}
              index={answer.index}
              findability={'find' == schema.mode_type || (1 == schema.max_cart_size && !schema.shopping_cart)}
              saveScrollPoint={saveScrollPoint}
            />
          ) : (
            ''
          )
        )}
      </div>
    </>
  )
}

export default Main
